import { useState, useEffect, useRef } from "react"
import { X, Camera, Radio, Trash2 } from "lucide-react"
import { CONVERSATION_NAME_MAX, CONVERSATION_NAME_MIN } from "./name-limits"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { AvatarPhoto } from "./avatar-photo"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { acceptAttributeFor, checkFileForUpload, getUploadPolicy } from "@/lib/upload-policy"

/**
 * What the edit screen hands back. The picture is a file still to upload, or
 * a removal; neither means it was left as it was.
 */
export type BroadcastEdit = { name: string; imageFile?: File; imageRemoved: boolean }

interface EditBroadcastPageProps {
  broadcastId: string
  currentName: string
  /** The broadcast's picture now, if it has one. */
  currentImage?: string
  onClose: () => void
  onSave: (data: BroadcastEdit) => void
}

export function EditBroadcastPage({
  broadcastId: _broadcastId,
  currentName,
  currentImage,
  onClose,
  onSave,
}: EditBroadcastPageProps) {
  const t = useTranslation()
  const [name, setName] = useState(currentName)
  const [hasChanges, setHasChanges] = useState(false)
  const [nameError, setNameError] = useState("")
  // The picture, as on Edit Group: a broadcast could be given one when it was
  // created and never changed or removed afterwards, because this screen
  // offered only the name.
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined)
  const [imageRemoved, setImageRemoved] = useState(false)
  // Picker filter from the server's policy; any image until it loads.
  const [imageAccept, setImageAccept] = useState("image/*")
  useEffect(() => {
    void getUploadPolicy().then(() => setImageAccept(acceptAttributeFor("image") || "image/*"))
  }, [])
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const imageChanged = selectedImage !== null || imageRemoved
    setHasChanges(name.trim() !== currentName || imageChanged)
  }, [name, currentName, selectedImage, imageRemoved])

  const displayImage = imageRemoved ? null : selectedImage || currentImage

  // Over the limit before a key is pressed: `maxLength` never applied to the
  // value we seeded from the stored name, so an existing over-long name is
  // shown as-is rather than silently truncated — losing someone's name on open
  // would be worse than asking them to shorten it.
  const overLimit = name.length > CONVERSATION_NAME_MAX

  const handleSave = () => {
    const trimmed = name.trim()
    if (trimmed.length < CONVERSATION_NAME_MIN) {
      setNameError(`Broadcast name must be at least ${CONVERSATION_NAME_MIN} characters`)
      return
    }
    // The missing half of the check. Without it an over-long name saved
    // cleanly, which is how a name longer than the stated maximum existed in
    // the first place.
    if (trimmed.length > CONVERSATION_NAME_MAX) {
      setNameError(`Broadcast name must be ${CONVERSATION_NAME_MAX} characters or fewer`)
      return
    }
    onSave({ name: trimmed, imageFile: selectedFile, imageRemoved })
  }

  const handlePickImage = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    const check = await checkFileForUpload(file, "image")
    if (!check.ok) {
      toast.error({ title: check.message, description: file.name })
      return
    }
    setSelectedFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => {
      setSelectedImage(ev.target?.result as string)
      setImageRemoved(false)
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveImage = () => {
    setSelectedImage(null)
    setSelectedFile(undefined)
    setImageRemoved(true)
  }

  return (
    <div className="flex flex-col h-full bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-4 border-b border-border shrink-0">
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <h2 className="flex-1 text-lg font-semibold text-foreground">Edit Broadcast</h2>
        <button
          onClick={handleSave}
          disabled={!hasChanges || overLimit}
          className="px-4 py-1.5 rounded-lg font-medium text-sm transition-colors disabled:text-muted-foreground text-primary hover:bg-primary/10 disabled:hover:bg-transparent"
        >
          Save
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Broadcast photo */}
        <div className="flex flex-col items-center mb-6">
          <div
            onClick={handlePickImage}
            className="relative cursor-pointer"
          >
            <div className="w-28 h-28 rounded-full bg-muted flex items-center justify-center overflow-hidden">
              <AvatarPhoto
                src={displayImage}
                alt="Broadcast"
                className="w-full h-full object-cover"
                fallback={<Radio className="w-12 h-12 text-muted-foreground" />}
              />
            </div>
            <div className="absolute -bottom-1 -right-1 w-9 h-9 bg-primary rounded-full border-4 border-background flex items-center justify-center">
              <Camera className="w-4 h-4 text-white" />
            </div>
          </div>
          <button
            onClick={handlePickImage}
            className="mt-3 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            {displayImage ? t('presence.changePhoto') : t('presence.addPhoto')}
          </button>
          <p className="mt-3 text-sm text-muted-foreground">
            Broadcast lists are a simple way to send messages to multiple people at once
          </p>
        </div>

        {/* Broadcast Name */}
        <div className="mb-6">
          <label className="block text-xs font-medium text-muted-foreground mb-2">Broadcast name</label>
          <input
            value={name}
            onChange={e => { setName(e.target.value); if (nameError) setNameError("") }}
            maxLength={CONVERSATION_NAME_MAX}
            placeholder="Enter broadcast name"
            className={`w-full rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none border ${nameError ? 'border-destructive bg-destructive/5' : 'border-none bg-muted'}`}
          />
          <div className="flex justify-between mt-2">
            {nameError ? <p className="text-xs text-destructive">{nameError}</p> : <span />}
            <p className={`text-xs ${overLimit ? "text-destructive" : "text-muted-foreground"}`}>
              {name.length}/{CONVERSATION_NAME_MAX}
            </p>
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 p-4 bg-muted/50 rounded-xl">
          <div className="flex items-start gap-3">
            <Radio className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Messages sent to a broadcast list are received as individual messages by each recipient. Recipients won't be able to see other recipients in the list.
            </p>
          </div>
        </div>

        {/* Remove Photo Option */}
        {displayImage && (
          <div className="mt-6">
            <button
              onClick={handleRemoveImage}
              className="flex items-center gap-2 text-destructive hover:text-destructive/80 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              <span className="text-sm font-medium">Remove Photo</span>
            </button>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={imageAccept}
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
    </div>
  )
}
